import { Input, Spin } from "antd";
import { ArrowRightOutlined, LoadingOutlined } from "@ant-design/icons";
import { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setToken } from "../../slices/profileSlice";
import useUserProfile from "../../hooks/useUserProfile";

import { signIn, _login_with_pin } from "../../common/axios_services";
import { navigateByUserRole } from "../../common/utils/navigationUtils";
import AppRoutes from "../../common/routes";
import GoogleAuthButton from "../../components/GoogleAuthButton";

const SignInPage = () => {
  const dispatch = useDispatch();
  let params = new URLSearchParams(window?.location?.search);
  let navigate = useNavigate();
  const antIcon = (
    <LoadingOutlined style={{ fontSize: 24, color: "#fff" }} spin />
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [fetchingData, setFetchingData] = useState(false);
  const [redir] = useState(params.get("redir") ? params.get("redir") : "");

  const [useAccountID, setUseAccountID] = useState(false);
  const { userProfile, refetchUserProfile } = useUserProfile();

  const signInSchema = useAccountID
    ? yup.object().shape({
        accountID: yup.string().required("Account ID is required"),
        loginPin: yup.string().required("Login Pin is required"),
      })
    : yup.object().shape({
        emailAddress: yup
          .string()
          .email("Email address is not valid")
          .required("Email address field is required"),
        password: yup.string().min(4).required("Password field is required"),
      });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValue: {
      accountID: "",
      loginPin: "",
      emailAddress: "",
      password: "",
    },
    resolver: yupResolver(signInSchema),
  });

  const fetchUser = async () => {
    try {
      let userData = await refetchUserProfile();

      if (userData?.email) {
        window.appilix_push_notification_user_identity = userData.email;
      }

      navigateByUserRole(userData, navigate, redir);
    } catch (err) {
      setFetchingData(false);
      if (err.response) {
        setErrorMessage(err.response.data.message);
      } else {
        setErrorMessage(err.message);
      }
    }
  };

  const signInUser = async (e) => {
    setErrorMessage("");
    setFetchingData(true);

    try {
      let response;
      if (useAccountID) {
        response = await _login_with_pin({
          accountId: e.accountID,
          pin: e.loginPin,
          app: "mobile",
        });
      } else {
        response = await signIn({
          email: e.emailAddress,
          password: e.password,
          app: "mobile",
        });
      }

      dispatch(setToken(response?.data?.data?.accessToken));
      localStorage.setItem("konnect_token", response?.data?.data?.accessToken);
      await fetchUser();
    } catch (err) {
      setFetchingData(false);
      setErrorMessage(err?.response?.data?.message ?? err?.message);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("konnect_token");
    if (token) {
      fetchUser();
    }
  }, []);

  return (
    <div className="signin-page">
      <div className="contain auth-content">
        <div className="mt_3 mb-24">
          <div className="auth-sect-div">
            <h3 className="auth-sect-title">Welcome to Konnect!</h3>
          </div>
          <div className="error-block">
            {errorMessage && <p className="form-error">{errorMessage}</p>}
          </div>
          <form
            onSubmit={handleSubmit(signInUser)}
            key={useAccountID ? "accountID" : "email"}
          >
            <div className="flex justify-between items-center mb-8">
              <button
                type="button"
                onClick={() => setUseAccountID(!useAccountID)}
                className="text-blue-700 font-gilroyBold text-[13px] py-2 px-5 rounded-sm bg-blue-100 "
              >
                {useAccountID
                  ? "Sign in using Email Address"
                  : "Sign in with Account ID"}
                <ArrowRightOutlined style={{ marginLeft: "3px" }} />
              </button>
            </div>
            <div>
              {useAccountID ? (
                <>
                  <div className="form_group">
                    <label htmlFor="accountID">Account ID</label>
                    <Controller
                      name="accountID"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          id="accountID"
                          type="tel"
                          style={{ height: "5rem" }}
                        />
                      )}
                    />
                    {errors.accountID && (
                      <p className="form-error">{errors.accountID.message}</p>
                    )}
                  </div>
                  <div className="form_group">
                    <label htmlFor="loginPin">Login Pin</label>
                    <Controller
                      name="loginPin"
                      control={control}
                      render={({ field }) => (
                        <Input.Password
                          {...field}
                          id="loginPin"
                          inputMode="numeric"
                          style={{ height: "5rem" }}
                        />
                      )}
                    />
                    {errors.loginPin && (
                      <p className="form-error">{errors.loginPin.message}</p>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="form_group">
                    <label htmlFor="emailAddress">Email Address</label>
                    <Controller
                      name="emailAddress"
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          id="emailAddress"
                          style={{ height: "5rem" }}
                        />
                      )}
                    />
                    {errors.emailAddress && (
                      <p className="form-error">
                        {errors.emailAddress.message}
                      </p>
                    )}
                  </div>
                  <div className="form_group">
                    <label htmlFor="password">Password</label>
                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => (
                        <Input.Password
                          {...field}
                          id="password"
                          style={{ height: "5rem" }}
                        />
                      )}
                    />
                    {errors.password && (
                      <p className="form-error">{errors.password.message}</p>
                    )}
                  </div>
                </>
              )}
              <div className="my-5 mb-24">
                <Link
                  className="inner-link"
                  to={useAccountID ? AppRoutes.reset_pin : AppRoutes.reset_mail}
                >
                  <span>
                    Click here to reset {useAccountID ? "pin" : "password"}.
                  </span>
                </Link>
              </div>
              <div className="welcome-buttons mt-15">
                <div className="welcome-buttons-content">
                  {!fetchingData ? (
                    <button className="btn-primary">
                      Log in to your account
                    </button>
                  ) : (
                    <button className="btn-primary" disabled>
                      <Spin indicator={antIcon} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </form>
          <div className="mt-10 text-center">
            <p className="text-2xl">
              Having problems logging in?{" "}
              <a
                href="https://tawk.to/chat/61590f9625797d7a8901fd89/default"
                target="_blank"
              >
                Chat with us
              </a>
            </p>
            <div className="welcome-buttons-content mt-8">
              <Link className="btn-border-primary" to={AppRoutes.signup}>
                Create an account
              </Link>
            </div>
            <GoogleAuthButton />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
